/**
 * Anniversary Main Banner — anniversary-main-banner.js
 * Place this file in: assets/anniversary-main-banner.js
 *
 * Responsibilities:
 *  1. Active-tab highlight on desktop (aria + class toggling)
 *  2. FAB (Floating Action Button) on mobile — builds the pill menu dynamically
 *     and handles open/close with smooth transition
 *  3. Shopify Theme Editor live-preview support
 */

(function () {
  'use strict';

  /* ── Helper: initialise one section instance ── */
  function initBannerSection(sectionEl) {

    /* ────────────────────────────────────────────
       1. DESKTOP: original glass-tabs behaviour
    ──────────────────────────────────────────── */
    const tabsGlass = sectionEl.querySelector('.anniversary-banner__tabs-glass');
    const tabs       = sectionEl.querySelectorAll('.anniversary-banner__tab');

    if (tabsGlass && tabs.length) {
      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          tabs.forEach(function (t) {
            t.classList.remove('is-active');
            t.setAttribute('aria-selected', 'false');
          });
          tab.classList.add('is-active');
          tab.setAttribute('aria-selected', 'true');

          /* ── Smooth-scroll to linked section ── */
          var targetId = tab.dataset.targetSection;
          if (targetId) {
            var targetEl = document.getElementById(targetId);
            if (targetEl) {
              var headerOffset = document.querySelector('.shopify-section-header') 
                ? document.querySelector('.shopify-section-header').offsetHeight 
                : 0;
              var y = targetEl.getBoundingClientRect().top + window.scrollY - headerOffset - 16;
              window.scrollTo({ top: y, behavior: 'smooth' });
            }
          }
        });
      });
    }

    /* ────────────────────────────────────────────
       2. MOBILE: build & wire FAB menu
    ──────────────────────────────────────────── */
    buildFab(sectionEl);
  }

  /* ── Build the FAB overlay from existing block data ── */
  function buildFab(sectionEl) {
    /* Gather tab data from the existing (desktop) tab buttons */
    const sourceTabs = Array.from(
      sectionEl.querySelectorAll('.anniversary-banner__tab')
    );
    if (!sourceTabs.length) return;

    /* Avoid double-build on Theme Editor reloads */
    if (sectionEl.querySelector('.anniversary-banner__fab-container')) return;

    /* ── Create FAB container ── */
    const fabContainer = document.createElement('div');
    fabContainer.className = 'anniversary-banner__fab-container';

    /* ── Tab list (sits above the FAB button) ── */
    const fabTabs = document.createElement('div');
    fabTabs.className = 'anniversary-banner__fab-tabs';
    fabTabs.setAttribute('role', 'list');

    /* Build a pill button for every source tab */
    sourceTabs.forEach(function (srcTab, i) {
      const iconEl  = srcTab.querySelector('.anniversary-banner__tab-icon');
      const labelEl = srcTab.querySelector('.anniversary-banner__tab-label');

      const fabTab = document.createElement('button');
      fabTab.className        = 'anniversary-banner__fab-tab';
      fabTab.setAttribute('role', 'listitem');
      fabTab.dataset.fabIndex = i;
      fabTab.dataset.tabIndex = srcTab.dataset.tabIndex || i;
      if (srcTab.dataset.targetSection) {
        fabTab.dataset.targetSection = srcTab.dataset.targetSection;
      }

      /* Icon */
      if (iconEl) {
        const iconWrapper = document.createElement('span');
        iconWrapper.className = 'anniversary-banner__fab-tab-icon';
        iconWrapper.setAttribute('aria-hidden', 'true');
        iconWrapper.innerHTML = iconEl.innerHTML;
        fabTab.appendChild(iconWrapper);
      }

      /* Label */
      if (labelEl) {
        const labelSpan = document.createElement('span');
        labelSpan.className   = 'anniversary-banner__fab-tab-label';
        labelSpan.textContent = labelEl.textContent.trim();
        fabTab.appendChild(labelSpan);
      }

      /* Mirror active state from desktop tab */
      if (srcTab.classList.contains('is-active')) {
        fabTab.classList.add('is-active');
      }

      /* Click: sync active state ↔ desktop tab, close menu, scroll */
      fabTab.addEventListener('click', function () {
        /* Update FAB tabs */
        fabTabs.querySelectorAll('.anniversary-banner__fab-tab').forEach(function (t) {
          t.classList.remove('is-active');
        });
        fabTab.classList.add('is-active');

        /* Sync to desktop tabs */
        const desktopTabs = sectionEl.querySelectorAll('.anniversary-banner__tab');
        desktopTabs.forEach(function (t) {
          t.classList.remove('is-active');
          t.setAttribute('aria-selected', 'false');
        });
        if (desktopTabs[i]) {
          desktopTabs[i].classList.add('is-active');
          desktopTabs[i].setAttribute('aria-selected', 'true');
        }

        /* Smooth-scroll to linked section */
        var targetId = srcTab.dataset.targetSection;
        if (targetId) {
          var targetEl = document.getElementById(targetId);
          if (targetEl) {
            var headerOffset = document.querySelector('.shopify-section-header')
              ? document.querySelector('.shopify-section-header').offsetHeight
              : 0;
            var y = targetEl.getBoundingClientRect().top + window.scrollY - headerOffset - 16;
            window.scrollTo({ top: y, behavior: 'smooth' });
          }
        }

        closeFab(fabContainer, backdrop);
      });

      fabTabs.appendChild(fabTab);
    });

    /* ── FAB trigger button ── */
    const fabBtn = document.createElement('button');
    fabBtn.className = 'anniversary-banner__fab-btn';
    fabBtn.setAttribute('aria-label', 'Open navigation menu');
    fabBtn.setAttribute('aria-expanded', 'false');

    /* Plus icon SVG */
    const iconWrapper = document.createElement('span');
    iconWrapper.className = 'anniversary-banner__fab-btn-icon';
    iconWrapper.setAttribute('aria-hidden', 'true');
    iconWrapper.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
           xmlns="http://www.w3.org/2000/svg">
        <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor"
              stroke-width="2" stroke-linecap="round"/>
        <line x1="5"  y1="12" x2="19" y2="12" stroke="currentColor"
              stroke-width="2" stroke-linecap="round"/>
      </svg>`;
    fabBtn.appendChild(iconWrapper);

    /* ── Backdrop (closes menu on outside tap) ── */
    const backdrop = document.createElement('div');
    backdrop.className = 'anniversary-banner__fab-backdrop';
    backdrop.setAttribute('aria-hidden', 'true');

    /* ── Wire FAB open/close ── */
    fabBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      if (fabContainer.classList.contains('is-open')) {
        closeFab(fabContainer, backdrop);
      } else {
        openFab(fabContainer, backdrop, fabBtn);
      }
    });

    backdrop.addEventListener('click', function () {
      closeFab(fabContainer, backdrop);
    });

    /* Escape key */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && fabContainer.classList.contains('is-open')) {
        closeFab(fabContainer, backdrop);
        fabBtn.focus();
      }
    });

    /* ── Assemble & inject ── */
    fabContainer.appendChild(fabTabs);
    fabContainer.appendChild(fabBtn);

    /* Insert inside the overlay so it's positioned relative to the banner */
    const overlay = sectionEl.querySelector('.anniversary-banner__overlay');
    if (overlay) {
  
      overlay.appendChild(fabContainer);
      /* Backdrop goes inside sectionEl so it covers only the banner */
      sectionEl.appendChild(backdrop);
    }
  }

  function openFab(container, backdrop, btn) {
    container.classList.add('is-open');
    backdrop.classList.add('is-visible');
    if (btn) btn.setAttribute('aria-expanded', 'true');
  }

  function closeFab(container, backdrop) {
    container.classList.remove('is-open');
    backdrop.classList.remove('is-visible');
    const btn = container.querySelector('.anniversary-banner__fab-btn');
    if (btn) btn.setAttribute('aria-expanded', 'false');
  }

  /* ── Boot: find all banner instances on the page ── */
  function bootAll() {
    document.querySelectorAll('.anniversary-banner').forEach(initBannerSection);
  }

  /* ── Shopify Theme Editor: re-init on section events ── */
  document.addEventListener('shopify:section:load', function (event) {
    var sectionEl = event.target.querySelector('.anniversary-banner');
    if (sectionEl) initBannerSection(sectionEl);
  });

  document.addEventListener('shopify:section:select', function (event) {
    var sectionEl = event.target.querySelector('.anniversary-banner');
    if (sectionEl) initBannerSection(sectionEl);
  });

  /* ── Run ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }
})();